const STALE_INACTIVE_MS = 20 * 60 * 60 * 1000;

export function draftHasRecoverableEncounter(draft: {
  persistedServiceId?: unknown;
  persistedDocId?: unknown;
  submissionStarted?: unknown;
} | null | undefined): boolean {
  if (!draft) return false;
  if (String(draft.persistedServiceId || '').trim()) return true;
  if (String(draft.persistedDocId || '').trim()) return true;
  return draft.submissionStarted === true;
}

/**
 * A never-submitted draft may be discarded. A draft that may already have
 * saved an encounter keeps the service and document ids.
 */
export function shouldDiscardStoredSoapDraft(
  draft: {
    selectedClient?: unknown;
    currentView?: unknown;
    submitted?: unknown;
    persistedServiceId?: unknown;
    persistedDocId?: unknown;
    submissionStarted?: unknown;
    lastActiveTimestamp?: unknown;
    startTimestamp?: unknown;
    timerState?: unknown;
    isTimerRunning?: unknown;
  } | null | undefined,
  nowMs: number
): boolean {
  if (draftHasRecoverableEncounter(draft)) return false;
  if (!draft?.selectedClient) return true;
  if (draft.currentView === 'success' || draft.submitted) return true;
  const lastActive = Number(draft.lastActiveTimestamp || draft.startTimestamp || 0);
  const running = draft.timerState === 'RUNNING' || Boolean(draft.isTimerRunning);
  if (!running && nowMs - lastActive > STALE_INACTIVE_MS) return true;
  return false;
}

/**
 * Documented duration from a stored draft. Uses the last paused/manual seconds.
 * Does not add time away, even when the draft still has a RUNNING timer flag.
 * Does not derive minutes from startTimeStr/endTimeStr (endTimeStr is unused by the form).
 */
export function documentedDurationSecondsFromDraft(draft: {
  accumulatedActiveSeconds?: unknown;
  elapsedSeconds?: unknown;
} | null | undefined): number {
  if (!draft) return 0;
  if (typeof draft.accumulatedActiveSeconds === 'number' && Number.isFinite(draft.accumulatedActiveSeconds)) {
    return Math.max(0, draft.accumulatedActiveSeconds);
  }
  if (typeof draft.elapsedSeconds === 'number' && Number.isFinite(draft.elapsedSeconds)) {
    return Math.max(0, draft.elapsedSeconds);
  }
  return 0;
}

/** Planned visit id wins. Otherwise reuse the id stored before the request. */
export function stableSubmittedServiceId(input: {
  plannedServiceId?: unknown;
  persistedServiceId?: unknown;
  encounterId?: unknown;
}): string {
  const planned = String(input.plannedServiceId || '').trim();
  if (planned) return planned;
  const persisted = String(input.persistedServiceId || '').trim();
  if (persisted) return persisted;
  const encounter = String(input.encounterId || '').trim();
  if (!encounter) return '';
  return encounter.startsWith('SRV-') ? encounter : `SRV-${encounter}`;
}
